import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { 
  onAuthStateChanged, 
  signInWithEmailAndPassword, 
  signOut 
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Fetch the user's profile and role from Firestore
        try {
          const userDoc = await getDoc(doc(db, "users", user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const isBlocked = userData.disabled === true || userData.status === 'Disabled' || userData.status === 'Inactive';
            
            if (isBlocked) {
              console.warn("User account is disabled or inactive.");
              await signOut(auth);
              setCurrentUser(null);
            } else {
              setCurrentUser({ uid: user.uid, email: user.email, ...userData });
            }
          } else {
            console.warn("User document not found in Firestore.");
            setCurrentUser({ uid: user.uid, email: user.email, role: 'Viewer' });
          }
        } catch (error) {
          console.error("Error fetching user profile:", error);
          setCurrentUser(user);
        }
      } else {
        setCurrentUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const login = async (email, password) => {
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      try {
        import('firebase/firestore').then(({ updateDoc, doc }) => {
          updateDoc(doc(db, "users", cred.user.uid), { lastLogin: new Date().toISOString() });
        });
      } catch (err) {
        console.warn("Failed to update last login", err);
      }
      return cred;
    } catch (err) {
      throw err;
    }
  };

  const logout = async () => {
    return signOut(auth);
  };

  const value = {
    currentUser,
    login,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
